import React from "react";
import ReactToPrint from "react-to-print";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import PrintIcon from "@mui/icons-material/Print";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LoadSpinner from "../../../../../utilities/LoadSpinner";

// Sheet is sized to US Letter landscape at 96dpi so the on-screen preview
// matches what comes out of the printer. Print CSS drops the fixed width so
// the browser's own page size (A4, Letter, etc.) is honoured.
export const SHEET_WIDTH_PX = 1056;
export const SHEET_SIZES = {
  landscape: { width: 1056, minHeight: 816 },
  portrait: { width: 816, minHeight: 1056 },
};

export const PRINT_CSS = `
  @media print {
    @page { size: landscape; margin: 10mm; }
    html, body { background: #fff !important; }
    body {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .print-sheet {
      width: auto !important;
      min-height: 0 !important;
      padding: 0 !important;
      margin: 0 !important;
      box-shadow: none !important;
      border: none !important;
    }
    .print-sheet table { width: 100%; border-collapse: collapse; }
    .print-sheet thead { display: table-header-group; }
    .print-sheet tfoot { display: table-footer-group; }
    .print-sheet tr,
    .print-avoid-break { break-inside: avoid; page-break-inside: avoid; }
    .print-page-break {
      break-before: page;
      page-break-before: always;
      margin-top: 0 !important;
      padding-top: 0 !important;
      border-top: none !important;
    }
    .print-accent {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  }
`;

export const violetCheckboxSx = {
  color: "#a78bfa",
  padding: "4px",
  "&.Mui-checked": { color: "#6d28d9" },
  "&.MuiCheckbox-indeterminate": { color: "#6d28d9" },
};

const selectSx = {
  backgroundColor: "white",
  borderRadius: "8px",
  height: "38px",
  fontSize: "13px",
  "& .MuiSelect-select": { paddingY: "8px" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#6d28d9" },
};

export function SettingsGroup({ title, children }) {
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-2">
        {title}
      </p>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

export function OptionToggle({ label, hint, checked, onChange, disabled }) {
  return (
    <label
      className={`flex items-start gap-1.5 -ml-1 rounded-md px-1 py-0.5 ${
        disabled ? "opacity-50" : "cursor-pointer hover:bg-slate-50"
      }`}
    >
      <Checkbox
        size="small"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        sx={violetCheckboxSx}
      />
      <span className="pt-[5px]">
        <span className="block text-[13px] text-slate-800 leading-tight">
          {label}
        </span>
        {hint && (
          <span className="block text-[11px] text-slate-400 leading-tight mt-0.5">
            {hint}
          </span>
        )}
      </span>
    </label>
  );
}

// Generic multi-select with checkboxes. `options` is [{value, label}].
// Pass `selectAll` to get a bold Select all / Clear row at the top.
export function MultiSelect({
  value,
  onChange,
  options,
  placeholder,
  selectAll = false,
}) {
  const allSelected = options.length > 0 && value.length === options.length;

  const handleChange = (event) => {
    const next = event.target.value;
    if (next.includes("__all__")) {
      onChange(allSelected ? [] : options.map((o) => o.value));
      return;
    }
    onChange(typeof next === "string" ? next.split(",") : next);
  };

  const labelFor = (v) => options.find((o) => o.value === v)?.label ?? v;

  return (
    <Select
      multiple
      fullWidth
      displayEmpty
      value={value}
      onChange={handleChange}
      sx={selectSx}
      MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
      renderValue={(selected) => {
        if (selected.length === 0) {
          return <span className="text-slate-400">{placeholder}</span>;
        }
        if (selectAll && allSelected) return "All";
        const labels = selected.map(labelFor);
        return labels.length <= 3
          ? labels.join(", ")
          : `${labels.slice(0, 3).join(", ")} +${labels.length - 3}`;
      }}
    >
      {selectAll && (
        <MenuItem value="__all__" dense>
          <Checkbox
            size="small"
            checked={allSelected}
            indeterminate={value.length > 0 && !allSelected}
            sx={violetCheckboxSx}
          />
          <ListItemText
            primary={allSelected ? "Clear all" : "Select all"}
            primaryTypographyProps={{ fontSize: 13, fontWeight: 600 }}
          />
        </MenuItem>
      )}
      {options.map((o) => (
        <MenuItem key={o.value} value={o.value} dense>
          <Checkbox
            size="small"
            checked={value.indexOf(o.value) > -1}
            sx={violetCheckboxSx}
          />
          <ListItemText
            primary={o.label}
            primaryTypographyProps={{ fontSize: 13 }}
          />
        </MenuItem>
      ))}
    </Select>
  );
}

// Small segmented control for short exclusive choices.
export function SegmentedControl({ value, onChange, options }) {
  return (
    <div className="flex rounded-lg border border-slate-300 p-0.5 bg-white">
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            disabled={o.disabled}
            title={o.title}
            onClick={() => onChange(o.value)}
            className={`flex-1 h-7 rounded-md text-[12px] font-medium ${
              active
                ? "bg-violet-700 text-white"
                : o.disabled
                ? "text-slate-300 cursor-not-allowed"
                : "text-slate-600 hover:bg-slate-100"
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export function PreviewEmptyState({ title, body }) {
  return (
    <div className="h-full min-h-[320px] flex flex-col items-center justify-center text-center px-8">
      <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center text-slate-500 mb-3">
        <PrintIcon fontSize="small" />
      </div>
      <p className="text-sm font-medium text-slate-700">{title}</p>
      {body && <p className="text-xs text-slate-500 mt-1 max-w-xs">{body}</p>}
    </div>
  );
}

/**
 * Common frame for every print mode: header with title, week and the Print
 * action; settings rail on the left; a paper-like preview on the right. The
 * preview node is the exact node that is printed, so what you see is what
 * you get.
 */
export default function PrintShell({
  title,
  subtitle,
  onClose,
  onBack,
  backLabel,
  settings,
  settingsFooter,
  canPrint = true,
  documentTitle,
  orientation = "landscape",
  modeSwitcher,
  loading = false,
  children,
}) {
  const sheetRef = React.useRef(null);
  const size = SHEET_SIZES[orientation] || SHEET_SIZES.landscape;

  return (
    <div className="h-full flex flex-col bg-white">
      <style>{PRINT_CSS}</style>
      <style>{`@media print { @page { size: ${orientation}; } }`}</style>

      <div className="flex items-center justify-between px-6 py-3.5 border-b border-slate-200 shrink-0">
        {/* Fixed width so the mode switcher doesn't shift as the
            title/subtitle change length between dates and modes. */}
        <div className="flex items-center gap-4 w-80 shrink-0 min-w-0">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900"
            >
              <ArrowBackIcon sx={{ fontSize: 16 }} />
              {backLabel || "Back"}
            </button>
          )}
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-slate-900 leading-tight truncate">
              {title}
            </h2>
            {subtitle && (
              <p className="text-xs text-slate-500 leading-tight mt-0.5 truncate">
                {subtitle}
              </p>
            )}
          </div>
        </div>
        {modeSwitcher && <div className="w-64 shrink-0 mx-4">{modeSwitcher}</div>}
        <div className="flex items-center gap-2">
          <ReactToPrint
            documentTitle={documentTitle}
            content={() => sheetRef.current}
            trigger={() => (
              <button
                type="button"
                disabled={!canPrint || loading}
                className="flex items-center gap-1.5 px-4 h-9 rounded-lg bg-violet-700 hover:bg-violet-800 text-white text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <PrintIcon sx={{ fontSize: 18 }} />
                Print
              </button>
            )}
          />
          <button
            type="button"
            onClick={onClose}
            aria-label="Close print view"
            className="w-9 h-9 flex items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-900"
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </button>
        </div>
      </div>

      <div className="flex flex-1 min-h-0">
        <aside className="w-72 shrink-0 border-r border-slate-200 overflow-y-auto flex flex-col">
          <div className="p-5 space-y-6 flex-1">{settings}</div>
          {settingsFooter && (
            <div className="p-5 border-t border-slate-200">{settingsFooter}</div>
          )}
        </aside>

        <main className="flex-1 min-w-0 overflow-auto bg-slate-100 p-8">
          <div
            ref={sheetRef}
            className="print-sheet bg-white shadow-md mx-auto text-slate-900"
            style={{
              width: size.width,
              minHeight: size.minHeight,
              padding: "36px 40px",
            }}
          >
            {loading ? (
              <div className="flex flex-col items-center justify-center gap-3 h-96 text-slate-500">
                <LoadSpinner />
                <p className="text-xs">Loading schedule…</p>
              </div>
            ) : (
              children
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
